import { useEffect, useState } from "react";
import { ArrowLeftRight, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { FibiLoading } from "../components/FibiLoading";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PinjamSaldoBanner } from "../components/PinjamSaldoBanner";
import { DatePicker } from "../components/DatePicker";

const monthStart = () => new Date().toISOString().slice(0, 8) + "01";
const today = () => new Date().toISOString().slice(0, 10);

export default function AgentMutasi() {
  const [logs, setLogs] = useState(null);
  const [f, setF] = useState({ from: monthStart(), to: today(), kind: "all" });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/balance/logs", { params: { date_from: f.from, date_to: f.to, ...(f.kind === "komisi" ? { kind: "komisi" } : {}) } });
      setLogs(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [f.kind]); // eslint-disable-line
  const rows = (logs || []).filter((l) => f.kind === "masuk" ? l.amount > 0 : f.kind === "keluar" ? l.amount < 0 : true);
  const tin = rows.filter((l) => l.amount > 0).reduce((a, l) => a + l.amount, 0);
  const tout = rows.filter((l) => l.amount < 0).reduce((a, l) => a + l.amount, 0);
  return (
    <div className="space-y-4" data-testid="agent-mutasi-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><ArrowLeftRight className="w-7 h-7 text-[#F04E23]" /> Mutasi Saldo</h1>
        <p className="text-sm text-muted-foreground mt-1">Riwayat saldo masuk, keluar, dan komisi akun Anda.</p>
      </div>
      <PinjamSaldoBanner testid="agent-mutasi-pinjam" />
      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1"><label className="text-[11px] text-muted-foreground">Dari</label><DatePicker value={f.from} onChange={(v) => setF({ ...f, from: v })} className="w-36" testid="agent-mutasi-from" /></div>
          <div className="space-y-1"><label className="text-[11px] text-muted-foreground">Sampai</label><DatePicker value={f.to} onChange={(v) => setF({ ...f, to: v })} className="w-36" testid="agent-mutasi-to" /></div>
          <div className="flex rounded-lg border border-border overflow-hidden text-xs font-mono" data-testid="agent-mutasi-kind">
            {[["all", "Semua", ""], ["masuk", "Masuk", "text-emerald-400"], ["keluar", "Keluar", "text-rose-400"], ["komisi", "Komisi", "text-amber-400"]].map(([k, l, cls]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, kind: k })} className={`px-3 py-1.5 transition-colors ${f.kind === k ? `bg-accent font-bold ${cls}` : "text-muted-foreground hover:bg-accent/50"}`} data-testid={`agent-mutasi-kind-${k}`}>{l}</button>
            ))}
          </div>
          <Button onClick={load} disabled={busy} data-testid="agent-mutasi-load">{busy ? "Memuat…" : "Tampilkan"}</Button>
          <Button variant="outline" size="icon" onClick={load} data-testid="agent-mutasi-refresh"><RefreshCcw className="w-4 h-4" /></Button>
        </CardContent>
      </Card>
      {logs && (
        <div className="flex gap-4 flex-wrap text-xs font-mono" data-testid="agent-mutasi-summary">
          <span className="text-emerald-400">Masuk: +{rupiah(tin)}</span>
          <span className="text-rose-400">Keluar: {rupiah(tout)}</span>
          <span className="text-sky-400">{rows.length} transaksi</span>
        </div>
      )}
      <Card className="bg-card border-border shadow-sm overflow-x-auto">
        <Table className="w-full table-fixed" data-testid="agent-mutasi-table">
          <TableHeader><TableRow><TableHead className="w-[46%]">Keterangan</TableHead><TableHead className="text-right w-[18%] px-1">Masuk</TableHead><TableHead className="text-right w-[18%] px-1">Keluar</TableHead><TableHead className="text-right w-[18%] px-1">Saldo</TableHead></TableRow></TableHeader>
          <TableBody>
            {busy && !rows.length && <TableRow><TableCell colSpan={4} className="p-0"><FibiLoading text="FIBI sedang memuat mutasi..." size={20} /></TableCell></TableRow>}
            {logs && !busy && !rows.length && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground" data-testid="agent-mutasi-empty">Tidak ada mutasi pada periode ini</TableCell></TableRow>}
            {rows.map((l) => (
              <TableRow key={l.id} data-testid={`agent-mutasi-row-${l.id}`}>
                <TableCell className="py-1.5 px-2">
                  <div className="text-[11px] font-medium break-words leading-snug">{l.note.replace(/ \(req [0-9a-f]+\)$/i, "")}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{fmtDateTime(l.created_at)}</div>
                </TableCell>
                <TableCell className="text-right font-mono font-semibold text-emerald-500 text-[11px] py-1 px-1">{l.amount > 0 ? `+${rupiah(l.amount)}` : "-"}{l.pct && <span className="ml-1 px-1 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-[9px] font-semibold">{l.pct}</span>}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-rose-500 text-[11px] py-1 px-1">{l.amount < 0 ? `-${rupiah(Math.abs(l.amount))}` : "-"}</TableCell>
                <TableCell className="text-right font-mono text-[11px] py-1 px-1">{l.balance_after != null ? rupiah(l.balance_after) : "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
